import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import yearOnYearEmissions from '../../../../services/modules/DASHBOARD/yearOnYearEmissions';
import {formatIndian} from '../../../../utils/formatIndian';
import {yearSelect} from '../../../../utils/FormUtils/yearSelect';

import {benchmarkDataIds} from './constants/commonConstants';
import {useOverviewInsight} from './OverviewInsightPanel';
import {
  BaselinePill,
  CategoryAxisTick,
  ChangePill,
  type ChartLayoutProps,
  getChartScales,
  activeCategoryOf,
  HoverHighlight,
  InsightToggleButton,
  numberFormatter,
  SCOPE_SERIES as SERIES,
  SCOPE_SPLIT_FOOTNOTE,
  ScopeLegendItems,
  ScopeTooltip,
  StickyEmissionsAxis
} from './SustainabilityChartParts';
import SustainabilityInsightPanel from './SustainabilityInsightPanel';

export type {
  SustainabilityInsight,
  SustainabilityInsightFactor
} from './SustainabilityAiInsight';

export interface SustainabilityKpiData {
  year: string;
  scope1: number;
  scope2: number;
  scope3: number;
}

interface YearOnYearSustainabilityPerformanceProps {
  data?: SustainabilityKpiData[];
  selectedYear?: string;
  // Toggles after emission calculation so this card refetches with the other KPIs.
  refreshKey?: boolean;
}

const TREND_COLOR = '#024064';
const TREND_GAP_COLOR = '#8BA7B8';
const BASELINE_LINE_COLOR = '#557F97';

interface SustainabilityChartRow {
  year: string;
  scope1: number | null;
  scope2: number | null;
  scope3: number | null;
  total: number | null;
  yoy?: number | null;
  isBaseline?: boolean;
}

// Every financial year from the first reported year up to the current one is
// plotted; years in between with nothing reported stay on the axis as gaps.
const buildContinuousSeries = (
  source: SustainabilityKpiData[]
): SustainabilityChartRow[] => {
  if (!source.length) {
    return [];
  }

  // yearSelect() is the platform-wide year list, newest first.
  const platformYears = yearSelect()
    .map((option) => option.value)
    .reverse();
  const byYear = new Map(source.map((item) => [item.year, item]));
  const reported = platformYears.filter((year) => byYear.has(year));

  if (!reported.length) {
    return [];
  }

  const startIndex = platformYears.indexOf(reported[0]);
  const span = platformYears.slice(startIndex);

  const rows: SustainabilityChartRow[] = [];
  let previousTotal: number | null = null;

  span.forEach((year, index) => {
    const item = byYear.get(year);

    if (!item) {
      rows.push({
        year,
        scope1: null,
        scope2: null,
        scope3: null,
        total: null
      });

      return;
    }

    const total = item.scope1 + item.scope2 + item.scope3;

    rows.push({
      ...item,
      total,
      // The first reported year is the baseline the chart compares against.
      isBaseline: index === 0,
      yoy:
        previousTotal === null || previousTotal === 0
          ? null
          : ((total - previousTotal) / previousTotal) * 100
    });

    previousTotal = total;
  });

  return rows;
};

// Minimum horizontal room per year, so bars and their labels stay readable.
// Once the span outgrows the panel the chart scrolls horizontally instead of
// squeezing the bars together.
const MIN_YEAR_COLUMN_WIDTH = 120;

// Smallest drawn segment, as a share of the tallest bar, so a non-zero scope
// that is tiny next to the others still shows up in the stack.
const MIN_VISIBLE_SEGMENT_SHARE = 0.02;

const formatCompactEmissions = (value: number): string =>
  formatIndian(value.toFixed(2));

interface OverlayProps extends ChartLayoutProps {
  rows: Array<SustainabilityChartRow & {drawnTotal: number | null}>;
  baselineTotal: number | null;
}

// Widest the hover highlight behind a year's bar may get.
const HIGHLIGHT_MAX_WIDTH = 209;

const LABEL_BOX_WIDTH = 168;
const LABEL_BOX_HEIGHT = 64;

// Value / change pills above each bar, "Not reported" markers for gap years
// and the baseline value tag, positioned from the chart's own scales.
const ChartOverlay: React.FC<OverlayProps> = ({
  rows,
  baselineTotal,
  ...layout
}) => {
  const {xScale, yScale, bandwidth} = getChartScales(layout);
  const {offset} = layout;

  if (!xScale || !yScale || !offset) {
    return null;
  }

  const zeroY = yScale(0);

  return (
    <g className="yoyPerformanceOverlay">
      {rows.map((row) => {
        const centerX = xScale(row.year) + bandwidth / 2;

        if (row.drawnTotal === null || row.total === null) {
          return (
            <foreignObject
              key={row.year}
              x={centerX - LABEL_BOX_WIDTH / 2}
              y={zeroY - 35}
              width={LABEL_BOX_WIDTH}
              height={26}
            >
              <div className="yoyPerformanceLabelStack">
                <span className="yoyPerformanceNotReported">Not reported</span>
              </div>
            </foreignObject>
          );
        }

        const topY = yScale(row.drawnTotal);
        const change = row.yoy ?? null;

        return (
          <foreignObject
            key={row.year}
            x={centerX - LABEL_BOX_WIDTH / 2}
            y={topY - LABEL_BOX_HEIGHT - 8}
            width={LABEL_BOX_WIDTH}
            height={LABEL_BOX_HEIGHT}
          >
            <div className="yoyPerformanceLabelStack">
              {change !== null ? <ChangePill change={change} /> : null}
              <span className="yoyPerformanceValue">
                {formatCompactEmissions(row.total)} tCO2e
              </span>
            </div>
          </foreignObject>
        );
      })}

      {baselineTotal !== null ? (
        <foreignObject
          x={offset.left + offset.width - 220}
          y={yScale(baselineTotal) - 28}
          width={220}
          height={24}
        >
          <div className="yoyPerformanceBaselineTag">
            <BaselinePill>
              Baseline {numberFormatter.format(baselineTotal)}{' '}
              <small>tCO2e</small>
            </BaselinePill>
          </div>
        </foreignObject>
      ) : null}
    </g>
  );
};

const YearOnYearSustainabilityPerformance: React.FC<
  YearOnYearSustainabilityPerformanceProps
> = ({data, selectedYear = '', refreshKey = false}) => {
  const insight = useOverviewInsight('yoy', selectedYear, refreshKey);
  const [isInsightOpen, setIsInsightOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [loadedData, setLoadedData] = React.useState<SustainabilityKpiData[]>(
    []
  );
  const [isLoading, setIsLoading] = React.useState(!data);

  React.useEffect(() => {
    if (data) {
      return;
    }

    let isActive = true;

    setIsLoading(true);
    yearOnYearEmissions(benchmarkDataIds.emissionsScopeWise)
      .then((rows) => {
        if (isActive) {
          setLoadedData(rows);
        }
      })
      .catch(() => {
        if (isActive) {
          setLoadedData([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [data, refreshKey]);

  const source = data ?? loadedData;

  const chartData = React.useMemo(
    () => buildContinuousSeries(source),
    [source]
  );

  // Reported tCO2e for the selected year, shown next to each scope in the
  // AI insight panel.
  const selectedYearScopes = React.useMemo(() => {
    const row = source.find((item) => item.year === selectedYear);

    return row
      ? {scope1: row.scope1, scope2: row.scope2, scope3: row.scope3}
      : undefined;
  }, [source, selectedYear]);

  const minVisibleSegment =
    Math.max(0, ...chartData.map((item) => item.total ?? 0)) *
    MIN_VISIBLE_SEGMENT_SHARE;

  // Drawn height of a segment: a non-zero value below the minimum is raised
  // to it so the scope stays visible; the tooltip still shows the real value.
  const visibleValue = React.useCallback(
    (value: number | null): number | null =>
      value !== null && value > 0 && value < minVisibleSegment
        ? minVisibleSegment
        : value,
    [minVisibleSegment]
  );

  const plotRows = React.useMemo(
    () =>
      chartData.map((row) => ({
        ...row,
        drawnScope1: visibleValue(row.scope1),
        drawnScope2: visibleValue(row.scope2),
        drawnScope3: visibleValue(row.scope3),
        drawnTotal:
          row.total === null
            ? null
            : (visibleValue(row.scope1) ?? 0) +
              (visibleValue(row.scope2) ?? 0) +
              (visibleValue(row.scope3) ?? 0)
      })),
    [chartData, visibleValue]
  );

  const activeYear = activeCategoryOf(
    plotRows,
    activeIndex,
    (row) => row.year
  );

  const baselineRow = chartData[0];
  const baselineTotal = baselineRow?.total ?? null;
  const firstYear = chartData[0]?.year;
  const lastYear = chartData[chartData.length - 1]?.year;

  return (
    <section
      className={`yoyPerformanceCard${
        isInsightOpen ? ' yoyPerformanceCardOpen' : ''
      }`}
    >
      <div className="yoyPerformanceHeader">
        <div className="yoyPerformanceTitle">
          <h2>Year-on-year sustainability performance</h2>
          <p>
            Total GHG emissions by scope, tCO2e
            {firstYear && lastYear ? ` · ${firstYear} – ${lastYear}` : ''}
          </p>
        </div>
        <InsightToggleButton
          open={isInsightOpen}
          onToggle={() => {
            setIsInsightOpen((open) => !open);
          }}
        />
      </div>

      <div className="yoyPerformanceBody">
        <div className="yoyPerformanceChartPanel">
          {isLoading || !chartData.length ? (
            <div className="yoyPerformanceChartEmpty">
              {isLoading
                ? 'Loading emissions data…'
                : 'No scope-wise emissions reported yet.'}
            </div>
          ) : (
            <div className="yoyPerformanceChartFrame">
              <StickyEmissionsAxis
                rows={plotRows}
                xKey="year"
                baseline={baselineTotal}
                includeTotalLine
              />
              <div className="yoyPerformanceChartScroll">
              <div
                className="yoyPerformanceChart"
                style={{minWidth: chartData.length * MIN_YEAR_COLUMN_WIDTH}}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={plotRows}
                    onMouseMove={(state: {activeTooltipIndex?: number}) => {
                      setActiveIndex(state.activeTooltipIndex ?? null);
                    }}
                    onMouseLeave={() => {
                      setActiveIndex(null);
                    }}
                    margin={{top: 72, right: 8, bottom: 0, left: 0}}
                    barSize={52}
                  >
                    <CartesianGrid
                      vertical={false}
                      stroke="rgba(2, 64, 100, 0.1)"
                      strokeDasharray="4 4"
                    />
                    <XAxis
                      dataKey="year"
                      axisLine={{stroke: TREND_COLOR}}
                      tickLine={false}
                      height={48}
                      interval={0}
                      tick={
                        <CategoryAxisTick baselineCategory={baselineRow?.year} />
                      }
                    />
                    <YAxis hide width={0} />
                    <Customized
                      component={
                        <HoverHighlight
                          activeCategory={activeYear}
                          maxWidth={HIGHLIGHT_MAX_WIDTH}
                        />
                      }
                    />
                    <Tooltip cursor={false} content={<ScopeTooltip />} />
                    {baselineTotal !== null ? (
                      <ReferenceLine
                        y={baselineTotal}
                        stroke={BASELINE_LINE_COLOR}
                        strokeDasharray="4 4"
                      />
                    ) : null}
                    <Bar
                      dataKey="drawnScope1"
                      stackId="emissions"
                      fill={SERIES[0].color}
                      stroke="#FFFFFF"
                      strokeWidth={1}
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="drawnScope2"
                      stackId="emissions"
                      fill={SERIES[1].color}
                      stroke="#FFFFFF"
                      strokeWidth={1}
                      radius={[2, 2, 2, 2]}
                    />
                    <Bar
                      dataKey="drawnScope3"
                      stackId="emissions"
                      fill={SERIES[2].color}
                      stroke="#FFFFFF"
                      strokeWidth={1}
                      radius={[6, 6, 2, 2]}
                    />
                    {/* Dashed link across years that were not reported. */}
                    <Line
                      dataKey="drawnTotal"
                      type="linear"
                      stroke={TREND_GAP_COLOR}
                      strokeDasharray="2 2"
                      strokeWidth={1}
                      connectNulls
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                    {/* Solid trend between consecutive reported years. */}
                    <Line
                      dataKey="drawnTotal"
                      type="linear"
                      stroke={TREND_COLOR}
                      strokeWidth={2}
                      dot={{
                        r: 4,
                        fill: '#FFFFFF',
                        stroke: TREND_COLOR,
                        strokeWidth: 2
                      }}
                      activeDot={{
                        r: 4,
                        fill: '#FFFFFF',
                        stroke: TREND_COLOR,
                        strokeWidth: 2
                      }}
                      isAnimationActive={false}
                    />
                    <Customized
                      component={
                        <ChartOverlay
                          rows={plotRows}
                          baselineTotal={baselineTotal}
                        />
                      }
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              </div>
            </div>
          )}

          <div className="yoyPerformanceLegend">
            <ScopeLegendItems />
            <span>
              <svg width="10" height="4" viewBox="0 0 10 4" aria-hidden="true">
                <path d="M0 2H10" stroke={TREND_COLOR} />
                <rect
                  x="3.5"
                  y="0.5"
                  width="3"
                  height="3"
                  rx="1.5"
                  fill="#FFFFFF"
                  stroke={TREND_COLOR}
                />
              </svg>
              Total trend
            </span>
            <span>
              <svg width="10" height="1" viewBox="0 0 10 1" aria-hidden="true">
                <line
                  y1="0.5"
                  x2="10"
                  y2="0.5"
                  stroke={TREND_COLOR}
                  strokeDasharray="4 1"
                />
              </svg>
              Baseline
            </span>
          </div>
          <p className="yoyPerformanceFootnote">{SCOPE_SPLIT_FOOTNOTE}</p>
        </div>

        {isInsightOpen ? (
          <SustainabilityInsightPanel
            status={insight.status}
            result={insight.result}
            scopeValues={selectedYearScopes}
          />
        ) : null}
      </div>
    </section>
  );
};

export default YearOnYearSustainabilityPerformance;
